import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  X, RefreshCw, AlertCircle, GitBranch, Clock, CheckCircle,
  XCircle, Workflow, Eye, Tag, Box, Layers,
  Image, FileText,ImageIcon,WorkflowIcon,  Maximize2, ChevronRight
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

// Softbox Component
function Softbox({ isOpen, onClose, imageUrl, title }) {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-90 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-colors z-10"
      >
        <X className="w-6 h-6 text-white" />
      </button>
      
      <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-4">
        {title && (
          <div className="text-white text-lg font-medium bg-black bg-opacity-50 px-4 py-2 rounded-lg">
            {title}
          </div>
        )}
        <img
          src={imageUrl}
          alt={title || 'Image'}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-lg">
          Click anywhere to close
        </div>
      </div>
    </div>
  );
}

// Custom Node Component
const WorkflowNodeViz = ({ data }) => {
  const isCompleted = data.status === 'completed';
  const isRunning = data.status === 'running';
  const needsUpdate = data.status === 'needs_update';
  const isFailed = data.status === 'failed';

  const bgColor = isCompleted ? 'bg-green-50' :
                  isRunning ? 'bg-blue-50' :
                  needsUpdate ? 'bg-yellow-50' :
                  isFailed ? 'bg-red-50' :
                  'bg-gray-50';

  const borderColor = isCompleted ? 'border-green-500' :
                      isRunning ? 'border-blue-500' :
                      needsUpdate ? 'border-yellow-500' :
                      isFailed ? 'border-red-500' :
                      'border-gray-400';

  const statusColor = isCompleted ? 'bg-green-500 text-green-50' :
                      isRunning ? 'bg-blue-500 text-blue-50' :
                      needsUpdate ? 'bg-yellow-500 text-yellow-50' :
                      isFailed ? 'bg-red-500 text-red-50' :
                      'bg-gray-500 text-gray-50';

  return (
    <div 
      className={`${bgColor} rounded-lg shadow-lg px-4 py-3 border-2 ${borderColor} min-w-[180px] relative hover:shadow-xl transition-all cursor-pointer`}
      onClick={() => data.onNodeClick && data.onNodeClick(data.nodeId)}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          width: '10px',
          height: '10px',
          backgroundColor: 'white',
          border: `2px solid ${isFailed ? '#ef4444' : '#3b82f6'}`,
        }}
      />

      <div className="space-y-1">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-bold text-xs text-gray-800 leading-tight">{data.label}</h4>
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColor} whitespace-nowrap`}>
            {data.status}
          </span>
        </div>

        <div className="text-xs text-gray-600">
          <div className="flex justify-between">
            <span className="font-medium">v{data.version}</span>
          </div>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          width: '10px',
          height: '10px',
          backgroundColor: 'white',
          border: `2px solid ${isFailed ? '#ef4444' : '#3b82f6'}`,
        }}
      />
    </div>
  );
};

// Image with Overlay Component
const ImageWithOverlay = ({ imageUrl, labelData, title, compact = false, onClick }) => {
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current) {
      const updateDimensions = () => {
        setImageDimensions({
          width: imageRef.current.width,
          height: imageRef.current.height
        });
      };
      
      if (imageRef.current.complete) {
        updateDimensions();
      } else {
        imageRef.current.onload = updateDimensions;
      }
    }
  }, [imageUrl]);

  const renderBoundingBox = (bboxData) => {
    if (!bboxData || !imageDimensions.width) return null;

    const { x1_percent, y1_percent, x2_percent, y2_percent } = bboxData;
    
    const left = (x1_percent / 100) * imageDimensions.width;
    const top = (y1_percent / 100) * imageDimensions.height;
    const width = ((x2_percent - x1_percent) / 100) * imageDimensions.width;
    const height = ((y2_percent - y1_percent) / 100) * imageDimensions.height;

    return (
      <div
        className="absolute border-2 border-green-500 bg-green-500 bg-opacity-20 pointer-events-none"
        style={{
          left: `${left}px`,
          top: `${top}px`,
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        {!compact && (
          <div className="absolute -top-6 left-0 bg-green-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {labelData.label_kind || 'Label'}
            {labelData.tag && ` - ${labelData.tag}`}
          </div>
        )}
      </div>
    );
  };

  const renderSegmentationMask = (segmentationData) => {
    if (!segmentationData?.mask || !imageDimensions.width) return null;

    return (
      <img
        src={segmentationData.mask}
        alt="Segmentation mask"
        className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none"
        style={{ mixBlendMode: 'multiply' }}
      />
    );
  };

  return (
    <div className="relative inline-block group">
      <img
        ref={imageRef}
        src={imageUrl}
        alt={title || "Output"}
        className={`${compact ? 'max-w-full' : 'max-w-md'} rounded-lg border-2 border-gray-300 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-blue-500`}
        onClick={onClick}
      />
      
      {labelData?.bbox_data && renderBoundingBox(labelData.bbox_data)}
      {labelData?.segmentation_data && renderSegmentationMask(labelData.segmentation_data)}
      
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="bg-black bg-opacity-60 rounded-full p-3">
          <Eye className="w-6 h-6 text-white" />
        </div>
      </div>
      
      {labelData && !compact && (
        <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
          {labelData.label_kind || 'Label'}
          {labelData.confidence && ` (${(labelData.confidence * 100).toFixed(1)}%)`}
        </div>
      )}
    </div>
  );
};

// Label Data Display Component
const LabelDataDisplay = ({ labelData, index, onImageClick }) => {
  const imageUrl = labelData.image_url;
  const hasBbox = labelData.bbox_data;
  const hasSegmentation = labelData.segmentation_data;
  
  return (
    <div className="bg-white rounded-lg border-2 border-blue-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5" />
            <h4 className="font-semibold">
              Label {index !== undefined ? `#${index + 1}` : ''}
            </h4>
          </div>
          <div className="flex items-center gap-2">
            {hasBbox && (
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                <Box className="w-3 h-3" />
                BBox
              </span>
            )}
            {hasSegmentation && (
              <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                <Layers className="w-3 h-3" />
                Mask
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <h5 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide flex items-center gap-1">
              <ImageIcon className="w-4 h-4" />
              Visual Data
            </h5>
            
            {imageUrl ? (
              <ImageWithOverlay 
                imageUrl={imageUrl} 
                labelData={labelData}
                title={`Label ${labelData.label_kind || ''}`}
                compact={true}
                onClick={(e) => {
                  e.stopPropagation();
                  onImageClick(imageUrl, `Label ${labelData.label_kind || ''}`, labelData);
                }}
              />
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No image available</p>
              </div>
            )}
          </div>

          {hasBbox && (
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <h5 className="text-xs font-semibold text-green-800 mb-2 uppercase tracking-wide flex items-center gap-1">
                <Box className="w-4 h-4" />
                Bounding Box
              </h5>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-white rounded p-2">
                  <span className="text-gray-500">X1:</span>
                  <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.x1_percent.toFixed(2)}%</span>
                </div>
                <div className="bg-white rounded p-2">
                  <span className="text-gray-500">Y1:</span>
                  <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.y1_percent.toFixed(2)}%</span>
                </div>
                <div className="bg-white rounded p-2">
                  <span className="text-gray-500">X2:</span>
                  <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.x2_percent.toFixed(2)}%</span>
                </div>
                <div className="bg-white rounded p-2">
                  <span className="text-gray-500">Y2:</span>
                  <span className="ml-1 font-mono text-gray-900">{labelData.bbox_data.y2_percent.toFixed(2)}%</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <h5 className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide flex items-center gap-1">
              <FileText className="w-4 h-4" />
              Label Information
            </h5>
            
            <div className="space-y-2">
              <div className="bg-white rounded-lg p-2">
                <p className="text-xs font-medium text-gray-500 mb-1">Label ID</p>
                <p className="text-xs font-mono text-gray-900 break-all">{labelData.id}</p>
              </div>

              {labelData.label_kind && (
                <div className="bg-white rounded-lg p-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">Kind</p>
                  <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                    {labelData.label_kind}
                  </span>
                </div>
              )}

              {labelData.tag && (
                <div className="bg-white rounded-lg p-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">Tag</p>
                  <span className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-semibold">
                    {labelData.tag}
                  </span>
                </div>
              )}

              {labelData.confidence !== null && labelData.confidence !== undefined && (
                <div className="bg-white rounded-lg p-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">Confidence</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-green-500 h-full transition-all"
                        style={{ width: `${labelData.confidence * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono text-gray-900 min-w-[3rem] text-right">
                      {(labelData.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {labelData.label_origin && (
                <div className="bg-white rounded-lg p-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">Origin</p>
                  <p className="text-xs text-gray-900">{labelData.label_origin}</p>
                </div>
              )}

              <div className="bg-white rounded-lg p-2">
                <p className="text-xs font-medium text-gray-500 mb-2">Timestamps</p>
                <div className="space-y-1">
                  {labelData.created_at && (
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <Clock className="w-3 h-3" />
                      <span className="text-gray-500">Created:</span>
                      <span className="font-mono">{new Date(labelData.created_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div
        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={() => setIsOpen(!isOpen)}
      >
        {Icon && <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />}
        <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`} />
        <div className="text-sm font-semibold text-gray-700 flex-1">{title}</div>
      </div>
      {isOpen && (
        <div className="mt-2 ml-6">
          {children}
        </div>
      )}
    </div>
  );
};

// Render Output Data
const OutputDataRenderer = ({ data, onImageClick, depth = 0 }) => {
  if (data === null || data === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }

  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    if (typeof data === 'string' && data.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return (
        <ImageWithOverlay 
          imageUrl={data} 
          title="Image"
          onClick={(e) => {
            e.stopPropagation();
            onImageClick(data, 'Image');
          }}
        />
      );
    }
    return <span className="text-gray-900 break-words">{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    const isLabelArray = data.length > 0 && data[0]?.label_kind !== undefined;
    
    if (isLabelArray) {
      return (
        <div className="space-y-4">
          {data.map((labelData, index) => (
            <LabelDataDisplay 
              key={labelData.id || index} 
              labelData={labelData} 
              index={index}
              onImageClick={onImageClick}
            />
          ))}
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        {data.map((item, index) => (
          <CollapsibleSection
            key={index}
            title={`Item ${index + 1}`}
            defaultOpen={depth === 0}
          >
            <OutputDataRenderer data={item} onImageClick={onImageClick} depth={depth + 1} />
          </CollapsibleSection>
        ))}
      </div>
    );
  }

  if (typeof data === 'object') {
    const isLabelData = data.label_kind !== undefined;
    
    if (isLabelData) {
      return <LabelDataDisplay labelData={data} onImageClick={onImageClick} />;
    }
    
    return (
      <div className="space-y-3">
        {Object.entries(data).map(([key, value]) => {
          const isComplex = typeof value === 'object' && value !== null;
          
          if (isComplex) {
            return (
              <CollapsibleSection
                key={key}
                title={key}
                defaultOpen={depth === 0}
                icon={key.includes('image') ? ImageIcon : null}
              >
                <OutputDataRenderer data={value} onImageClick={onImageClick} depth={depth + 1} />
              </CollapsibleSection>
            );
          }
          
          return (
            <div key={key} className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm font-semibold text-gray-700 mb-2">{key}</div>
              <div className="text-sm">
                <OutputDataRenderer data={value} onImageClick={onImageClick} depth={depth + 1} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return <span className="text-gray-900">{String(data)}</span>;
};

// Node Detail Modal
function WorkflowNodeDetailModal({ isOpen, onClose, nodeId }) {
  const { authenticatedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nodeDetail, setNodeDetail] = useState(null);
  const [softboxOpen, setSoftboxOpen] = useState(false);
  const [softboxImage, setSoftboxImage] = useState(null);

  useEffect(() => {
    if (!isOpen || !nodeId) return;

    const fetchNodeDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/workflows2/workflow-node/${nodeId}/detail`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setNodeDetail(data);
      } catch (err) {
        console.error('Error fetching node detail:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNodeDetail();
  }, [isOpen, nodeId, authenticatedFetch]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'needs_update': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'needs_update': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const handleImageClick = (url, title, labelData = null) => {
    setSoftboxImage({ url, title, labelData });
    setSoftboxOpen(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GitBranch className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">Workflow Node Details</h2>
                {nodeDetail && (
                  <p className="text-purple-100 text-sm mt-1">
                    {nodeDetail.workflow_item_name || 'Node'}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-4">
                  <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                  <p className="text-gray-600 font-medium">Loading node details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Error loading node details</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            ) : nodeDetail ? (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">Node ID</p>
                        <p className="text-sm font-mono text-gray-900 break-all">{nodeDetail.id}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">Workflow Item ID</p>
                        <p className="text-sm font-mono text-gray-900 break-all">{nodeDetail.workflow_item_id}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(nodeDetail.status)}`}>
                          {getStatusIcon(nodeDetail.status)}
                          {nodeDetail.status}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">Version</p>
                        <p className="text-sm font-semibold text-gray-900">v{nodeDetail.version}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {nodeDetail.output_data && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Maximize2 className="w-5 h-5 text-indigo-600" />
                        Output Data
                      </h3>
                    </div>
                    <div className="p-6">
                      <OutputDataRenderer 
                        data={nodeDetail.output_data} 
                        onImageClick={handleImageClick}
                      />
                    </div>
                  </div>
                )}

                {!nodeDetail.output_data && (
                  <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">No output data available</p>
                    <p className="text-sm text-gray-500 mt-1">This node hasn't produced any output yet</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Softbox
        isOpen={softboxOpen}
        onClose={() => setSoftboxOpen(false)}
        imageUrl={softboxImage?.url}
        title={softboxImage?.title}
      />
    </>
  );
}

// Main Workflow Detail Modal
function WorkflowDetailModalComponent({ isOpen, onClose, workflowId }) {
  const { authenticatedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodeModalOpen, setNodeModalOpen] = useState(false);
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const nodeTypes = useMemo(() => ({ workflowNode: WorkflowNodeViz }), []);

  const handleNodeClick = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
    setNodeModalOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen || !workflowId) return;

    const fetchWorkflowDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/workflows2/workflow/${workflowId}/detail`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setWorkflowData(data);
      } catch (err) {
        console.error('Error fetching workflow detail:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowDetail();
  }, [isOpen, workflowId, authenticatedFetch]);

  // Build ReactFlow visualization
  useEffect(() => {
    if (!workflowData?.nodes || workflowData.nodes.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    buildWorkflowVisualization();
  }, [workflowData]);

  const buildWorkflowVisualization = () => {
    const workflowNodes = workflowData.nodes;
    const dependencies = workflowData.dependencies || [];

    // Build dependency graph
    const graph = {};
    workflowNodes.forEach(node => {
      graph[node.id] = {
        node,
        dependencies: [],
        dependents: []
      };
    });

    dependencies.forEach(dep => {
      if (graph[dep.node_id] && graph[dep.depends_on_node_id]) {
        graph[dep.node_id].dependencies.push(dep.depends_on_node_id);
        graph[dep.depends_on_node_id].dependents.push(dep.node_id);
      }
    });

    // Calculate levels (topological sort)
    const levels = {};
    const calculateLevel = (nodeId, visitedInPath = new Set()) => {
      if (visitedInPath.has(nodeId)) return 0;
      if (nodeId in levels) return levels[nodeId];
      
      visitedInPath.add(nodeId);
      const nodeData = graph[nodeId];
      
      if (!nodeData || nodeData.dependencies.length === 0) {
        levels[nodeId] = 0;
      } else {
        let maxDepLevel = -1;
        nodeData.dependencies.forEach(depId => {
          const depLevel = calculateLevel(depId, new Set(visitedInPath));
          maxDepLevel = Math.max(maxDepLevel, depLevel);
        });
        levels[nodeId] = maxDepLevel + 1;
      }
      
      visitedInPath.delete(nodeId);
      return levels[nodeId];
    };

    Object.keys(graph).forEach(nodeId => calculateLevel(nodeId));

    // Convert to level array
    const levelArray = [];
    Object.entries(levels).forEach(([nodeId, level]) => {
      if (!levelArray[level]) levelArray[level] = [];
      levelArray[level].push(nodeId);
    });

    // Create ReactFlow nodes
    const flowNodes = [];
    const nodeSpacingX = 250;
    const nodeSpacingY = 150;
    const startY = 50;

    levelArray.forEach((level, levelIndex) => {
      const nodesInLevel = level.length;
      const startX = (800 - (nodesInLevel * nodeSpacingX)) / 2;
      
      level.forEach((nodeId, index) => {
        const nodeData = workflowNodes.find(n => n.id === nodeId);
        if (!nodeData) return;

        flowNodes.push({
          id: nodeId,
          type: 'workflowNode',
          position: {
            x: startX + index * nodeSpacingX,
            y: startY + levelIndex * nodeSpacingY
          },
          data: {
            label: nodeData.workflow_item_name || 'Unknown',
            status: nodeData.status,
            version: nodeData.version,
            nodeId: nodeId,
            onNodeClick: handleNodeClick
          },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        });
      });
    });

    // Create ReactFlow edges
    const flowEdges = [];
    dependencies.forEach(dep => {
      const sourceNode = workflowNodes.find(n => n.id === dep.depends_on_node_id);
      const targetNode = workflowNodes.find(n => n.id === dep.node_id);
      
      if (!sourceNode || !targetNode) return;

      const edgeColor = 
        targetNode.status === 'completed' ? '#10b981' :
        targetNode.status === 'failed' ? '#ef4444' :
        targetNode.status === 'running' ? '#3b82f6' :
        targetNode.status === 'needs_update' ? '#f59e0b' :
        '#6b7280';

      flowEdges.push({
        id: `e-${dep.depends_on_node_id}-${dep.node_id}`,
        source: dep.depends_on_node_id,
        target: dep.node_id,
        type: 'smoothstep',
        animated: targetNode.status === 'running',
        style: { stroke: edgeColor, strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColor,
        },
        label: dep.consumed_version ? `v${dep.consumed_version}` : '',
        labelStyle: { 
          fill: edgeColor, 
          fontWeight: 600,
          fontSize: 9
        },
        labelBgStyle: { 
          fill: 'white',
          fillOpacity: 0.8
        },
      });
    });

    setNodes(flowNodes);
    setEdges(flowEdges);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'needs_update': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'template': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'needs_update': return <AlertCircle className="w-4 h-4" />;
      case 'template': return <Layers className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const calculateProgress = () => {
    if (!workflowData?.nodes || workflowData.nodes.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    const completed = workflowData.nodes.filter(n => n.status === 'completed').length;
    const total = workflowData.nodes.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  if (!isOpen) return null;

  const progress = calculateProgress();

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6 flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <WorkflowIcon className="w-6 h-6" />
                Workflow Details
              </h2>
              {workflowData && (
                <div className="mt-2 flex items-center gap-4">
                  <p className="text-indigo-100">
                    <span className="font-medium">{workflowData.name}</span>
                    {workflowData.identifier && (
                      <span className="ml-2 text-sm opacity-90">({workflowData.identifier})</span>
                    )}
                  </p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border-2 ${getStatusColor(workflowData.status)}`}>
                    {getStatusIcon(workflowData.status)}
                    {workflowData.status}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-4">
                  <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
                  <p className="text-gray-600 font-medium">Loading workflow details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Error loading details</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            ) : workflowData ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Workflow Information</h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">ID</p>
                        <p className="text-sm font-mono text-gray-900 break-all">{workflowData.id}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">Identifier</p>
                        <p className="text-sm font-mono text-gray-900">{workflowData.identifier}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">Kind</p>
                        <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                          {workflowData.kind}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">Version</p>
                        <p className="text-sm font-semibold text-gray-900">v{workflowData.version}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs font-medium text-gray-500 mb-2">Progress</p>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Nodes completed</span>
                            <span className="font-semibold text-gray-900">
                              {progress.completed}/{progress.total}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-green-500 h-full transition-all duration-500"
                              style={{ width: `${progress.percentage}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 text-right">{progress.percentage}% complete</p>
                        </div>
                      </div>
                      {workflowData.created_at && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-xs font-medium text-gray-500 mb-1">Created</p>
                          <p className="text-sm text-gray-900">
                            {new Date(workflowData.created_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {workflowData.updated_at && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-xs font-medium text-gray-500 mb-1">Updated</p>
                          <p className="text-sm text-gray-900">
                            {new Date(workflowData.updated_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 280px)' }}>
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <GitBranch className="w-5 h-5 text-indigo-600" />
                        Workflow Visualization
                        <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                          {workflowData.nodes?.length || 0} nodes
                        </span>
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">Click on nodes to view details</p>
                    </div>
                    
                    <div className="h-full">
                      {workflowData.nodes && workflowData.nodes.length > 0 ? (
                        <ReactFlow
                          nodes={nodes}
                          edges={edges}
                          onNodesChange={onNodesChange}
                          onEdgesChange={onEdgesChange}
                          nodeTypes={nodeTypes}
                          fitView
                          proOptions={{ hideAttribution: true }}
                          minZoom={0.1}
                          maxZoom={1.5}
                        >
                          <Controls className="bg-white" />
                          <MiniMap 
                            nodeColor={(node) => {
                              const status = node.data.status;
                              if (status === 'completed') return '#10b981';
                              if (status === 'running') return '#3b82f6';
                              if (status === 'needs_update') return '#f59e0b';
                              if (status === 'failed') return '#ef4444';
                              return '#9ca3af';
                            }}
                            className="bg-white border border-gray-200"
                            style={{ height: 100 }}
                          />
                          <Background variant="dots" gap={12} size={1} />
                        </ReactFlow>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <div className="text-center">
                            <GitBranch className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg font-medium">No nodes found</p>
                            <p className="text-sm mt-1">This workflow doesn't have any nodes yet</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <WorkflowNodeDetailModal
        isOpen={nodeModalOpen}
        onClose={() => setNodeModalOpen(false)}
        nodeId={selectedNodeId}
      />
    </>
  );
}

export default WorkflowDetailModalComponent;